import React, { useState } from 'react';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent,
} from '@mui/lab';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Avatar,
  Stack,
  Divider,
  Collapse,
  IconButton,
} from '@mui/material';
import {
  NoteAddOutlined,
  EmailOutlined,
  PhoneOutlined,
  EventOutlined,
  TrendingUpOutlined,
  PersonOutlined,
  EditOutlined,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import zhTW from 'date-fns/locale/zh-TW';

export interface Activity {
  id: string;
  type: 'NOTE' | 'EMAIL' | 'CALL' | 'MEETING' | 'STATUS_CHANGE' | 'SCORE_UPDATE' | 'ASSIGNMENT';
  subject?: string;
  content: string;
  performedBy: {
    id: string;
    name: string;
    email?: string;
  };
  createdAt: Date | string;
  metadata?: {
    oldValue?: string;
    newValue?: string;
    oldScore?: number;
    newScore?: number;
    emailSubject?: string;
    meetingDate?: Date | string;
    callDuration?: number;
  };
}

interface ActivityTimelineProps {
  activities: Activity[];
  maxHeight?: number | string;
  emptyMessage?: string;
}

const getActivityIcon = (type: Activity['type']) => {
  switch (type) {
    case 'NOTE': return <NoteAddOutlined />;
    case 'EMAIL': return <EmailOutlined />;
    case 'CALL': return <PhoneOutlined />;
    case 'MEETING': return <EventOutlined />;
    case 'STATUS_CHANGE': return <EditOutlined />;
    case 'SCORE_UPDATE': return <TrendingUpOutlined />;
    case 'ASSIGNMENT': return <PersonOutlined />;
    default: return <NoteAddOutlined />;
  }
};

const getActivityColor = (type: Activity['type']): 'primary' | 'success' | 'info' | 'warning' | 'error' => {
  switch (type) {
    case 'NOTE': return 'info';
    case 'EMAIL': return 'primary';
    case 'CALL': return 'success';
    case 'MEETING': return 'warning';
    case 'STATUS_CHANGE': return 'info';
    case 'SCORE_UPDATE': return 'success';
    case 'ASSIGNMENT': return 'primary';
    default: return 'info';
  }
};

const getActivityTypeLabel = (type: Activity['type']): string => {
  switch (type) {
    case 'NOTE': return 'Note 備註';
    case 'EMAIL': return 'Email 電郵';
    case 'CALL': return 'Call 通話';
    case 'MEETING': return 'Meeting 會議';
    case 'STATUS_CHANGE': return 'Status Change 狀態變更';
    case 'SCORE_UPDATE': return 'AI Score Update 評分更新';
    case 'ASSIGNMENT': return 'Assignment 分配';
    default: return 'Activity 活動';
  }
};

// Returns a short preview (first line / first 80 chars) for collapsed state
const getPreview = (activity: Activity): string => {
  const text = activity.subject || activity.metadata?.emailSubject || activity.content || '';
  const firstLine = text.split('\n')[0];
  return firstLine.length > 80 ? firstLine.slice(0, 80) + '…' : firstLine;
};

// Full content shown when expanded
const formatActivityContent = (activity: Activity): React.ReactNode => {
  const { type, content, metadata } = activity;

  switch (type) {
    case 'STATUS_CHANGE':
      return (
        <Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Status changed from{' '}
            <Chip label={metadata?.oldValue || 'N/A'} size="small" sx={{ mx: 0.5 }} /> to{' '}
            <Chip label={metadata?.newValue || 'N/A'} size="small" color="primary" sx={{ mx: 0.5 }} />
          </Typography>
          {content && <Typography variant="body2" sx={{ mt: 1 }}>{content}</Typography>}
        </Box>
      );

    case 'SCORE_UPDATE': {
      const scoreDiff = (metadata?.newScore ?? 0) - (metadata?.oldScore ?? 0);
      return (
        <Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            AI Score updated: {metadata?.oldScore || 0} → {metadata?.newScore || 0}{' '}
            <Chip
              label={scoreDiff > 0 ? `+${scoreDiff}` : scoreDiff}
              size="small"
              color={scoreDiff > 0 ? 'success' : 'error'}
              sx={{ ml: 0.5 }}
            />
          </Typography>
          {content && <Typography variant="body2" sx={{ mt: 1 }}>{content}</Typography>}
        </Box>
      );
    }

    case 'EMAIL':
      return (
        <Box>
          {metadata?.emailSubject && (
            <Typography variant="body2" fontWeight="medium" gutterBottom>
              Subject: {metadata.emailSubject}
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
            {content}
          </Typography>
        </Box>
      );

    case 'CALL':
      return (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
            {content}
          </Typography>
          {metadata?.callDuration && (
            <Chip label={`${metadata.callDuration} min`} size="small" sx={{ mt: 1 }} />
          )}
        </Box>
      );

    case 'MEETING':
      return (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
            {content}
          </Typography>
          {metadata?.meetingDate && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Scheduled: {new Date(metadata.meetingDate).toLocaleString('en-US', {
                year: 'numeric', month: 'short', day: 'numeric',
                hour: '2-digit', minute: '2-digit',
              })}
            </Typography>
          )}
        </Box>
      );

    default:
      return (
        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
          {content}
        </Typography>
      );
  }
};

// Determine if an activity needs expand/collapse (has meaningful content beyond the preview)
const needsExpand = (activity: Activity): boolean => {
  if (activity.type === 'STATUS_CHANGE' || activity.type === 'SCORE_UPDATE') return false;
  const text = activity.content || '';
  const hasSubject = !!(activity.subject || activity.metadata?.emailSubject);
  return hasSubject || text.length > 80 || text.includes('\n');
};

const ActivityCard: React.FC<{ activity: Activity }> = ({ activity }) => {
  const [expanded, setExpanded] = useState(false);
  const canExpand = needsExpand(activity);

  return (
    <Paper
      elevation={1}
      sx={{
        p: 2,
        backgroundColor: 'background.paper',
        cursor: canExpand ? 'pointer' : 'default',
        '&:hover': canExpand ? { bgcolor: 'action.hover' } : {},
        transition: 'background-color 0.15s',
      }}
      onClick={() => canExpand && setExpanded((v) => !v)}
    >
      {/* Header row: type chip + subject/preview + expand toggle */}
      <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ flex: 1, minWidth: 0 }}>
          <Chip
            label={getActivityTypeLabel(activity.type)}
            size="small"
            color={getActivityColor(activity.type)}
          />
          {activity.subject && (
            <Typography variant="body2" fontWeight="medium" noWrap>
              {activity.subject}
            </Typography>
          )}
          {/* Show preview text when collapsed and no subject */}
          {!expanded && !activity.subject && (
            <Typography variant="body2" color="text.secondary" noWrap sx={{ flex: 1, minWidth: 0 }}>
              {getPreview(activity)}
            </Typography>
          )}
        </Stack>
        {canExpand && (
          <IconButton size="small" sx={{ flexShrink: 0, ml: 0.5 }}>
            {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        )}
      </Stack>

      {/* Expanded full content */}
      <Collapse in={expanded || !canExpand} timeout="auto">
        <Box sx={{ mt: 1.5 }}>
          {formatActivityContent(activity)}
        </Box>
      </Collapse>

      <Divider sx={{ my: 1.5 }} />

      <Stack direction="row" spacing={1} alignItems="center">
        <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem' }}>
          {activity.performedBy.name.charAt(0).toUpperCase()}
        </Avatar>
        <Typography variant="caption" color="text.secondary">
          {activity.performedBy.name}
        </Typography>
      </Stack>
    </Paper>
  );
};

const ActivityTimeline: React.FC<ActivityTimelineProps> = ({
  activities,
  maxHeight = 600,
  emptyMessage = 'No activities yet 尚無活動記錄',
}) => {
  if (!activities || activities.length === 0) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 4,
          textAlign: 'center',
          backgroundColor: 'grey.50',
          border: '1px dashed',
          borderColor: 'grey.300',
        }}
      >
        <NoteAddOutlined sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
        <Typography variant="body1" color="text.secondary">
          {emptyMessage}
        </Typography>
      </Paper>
    );
  }

  return (
    <Box
      sx={{
        maxHeight,
        overflowY: 'auto',
        pr: 1,
        '&::-webkit-scrollbar': { width: '8px' },
        '&::-webkit-scrollbar-track': { backgroundColor: 'grey.100', borderRadius: '4px' },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'grey.400',
          borderRadius: '4px',
          '&:hover': { backgroundColor: 'grey.500' },
        },
      }}
    >
      <Timeline position="right">
        {activities.map((activity, index) => (
          <TimelineItem key={activity.id}>
            <TimelineOppositeContent
              sx={{ maxWidth: '120px', px: 1, flex: '0 0 120px' }}
            >
              <Typography variant="caption" color="text.secondary">
                {formatDistanceToNow(new Date(activity.createdAt), {
                  addSuffix: true,
                  locale: zhTW,
                })}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                {new Date(activity.createdAt).toLocaleString('en-US', {
                  month: 'short', day: 'numeric',
                  hour: '2-digit', minute: '2-digit',
                })}
              </Typography>
            </TimelineOppositeContent>

            <TimelineSeparator>
              <TimelineDot color={getActivityColor(activity.type)} variant="outlined">
                {getActivityIcon(activity.type)}
              </TimelineDot>
              {index < activities.length - 1 && <TimelineConnector />}
            </TimelineSeparator>

            <TimelineContent sx={{ pb: 3 }}>
              <ActivityCard activity={activity} />
            </TimelineContent>
          </TimelineItem>
        ))}
      </Timeline>
    </Box>
  );
};

export default React.memo(ActivityTimeline);
